<template>
    <transition name="fade">
        <div v-show="visible" c2c-0 class="popup-overlay">
            <div c2c-1 class="popup-content">
                <button
                    c2c-2 class="popup-close"
                    @click="close"
                    aria-label="Close popup"
                >
                    ✕
                </button>

                <div c2c-3 class="text-center">
                    <h2 class="title">🎉 Spin & Win</h2>
                    <p c2c-4 class="subtitle">Spin the wheel for a discount on your Team Banner!</p>
                </div>

                <div c2c-5 class="email-check">
                    <input
                        type="email"
                        v-model.trim="email"
                        c2c-6 class="email-input"
                        placeholder="Enter your email to spin"
                        :disabled="checking || (played && lockAfterWin)"
                        aria-label="Email address"
                    />
                    <button
                        c2c-7 class="check-btn"
                        @click="checkEligibility"
                        :disabled="checking || (played && lockAfterWin)"
                    >
                        <span v-if="!checking">Check</span>
                        <span v-else>Checking…</span>
                    </button>
                    <div c2c-8 class="api-message" v-if="apiMessage">
                        {{ apiMessage }}<span v-if="spinsLeft !== null" style="color: red"> (Spins left: {{ spinsLeft }})</span>
                    </div>
                </div>

                <div c2c-9 class="wheel-wrapper">
                    <div c2c-10 class="wheel-stage" :style="{ width: canvasSize + 'px', height: canvasSize + 'px' }">
                        <!-- Pointer (bottom) -->
                        <div c2c-11 class="wheel-pointer" aria-hidden="true">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2 L20 14 H4 Z" :fill="pointerColor"/>
                                <circle cx="12" cy="14.5" r="2.2" fill="#ffffff" stroke="#0f172a" stroke-width="1"/>
                            </svg>
                        </div>
                        <canvas
                            ref="wheelCanvas"
                            :width="canvasSize"
                            :height="canvasSize"
                            c2c-12 class="wheel-canvas"
                            aria-label="Prize wheel"
                        />
                    </div>

                    <div c2c-13 class="spin-btn-wrapper">
                        <button
                            :disabled="spinning || (played && lockAfterWin) || !eligible || (spinsLeft !== null && spinsLeft <= 0)"
                            @click="spin"
                            c2c-14 class="spin-btn"
                        >
                            <span v-if="!spinning && !(played && lockAfterWin)">SPIN</span>
                            <span v-else-if="spinning">Spinning…</span>
                            <span v-else>Claimed</span>
                        </button>
                    </div>
                </div>

                <!-- Structured result: message + code row (Use button next to code) -->
                <div v-if="promotion && promotion.code" c2c-15 class="result-msg">
                    <div>{{ message }}</div>
                    <div c2c-16 class="code-row">
                        <span>Code: <strong>{{ promotion.code }}</strong></span>
                        <button c2c-17 class="use-btn" @click="useCode" :disabled="codeUsed">
                            <span v-if="!codeUsed">Use</span>
                            <span v-else>Used</span>
                        </button>
                    </div>
                </div>
                <!-- Fallback to simple text when there's no promotion (e.g., lose) -->
                <p v-else-if="message" c2c-18 class="result-msg">{{ message }}</p>
                <p v-if="claiming" c2c-19 class="api-message">Generating your code…</p>

                <div c2c-20 class="disclaimer">
                    <slot name="disclaimer">One spin per visitor. Terms apply.</slot>
                </div>
            </div>
        </div>
    </transition>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch, computed } from 'vue';
import {IPromotion} from "../../interface/IPromotion";

import {useCartStore} from "../../../pages/(web)/cart.store";

const {setPromo} = useCartStore();



export interface Prize {
    label: string
    /** Fixed amount or percent based on type */
    value?: number
    type?: 'percent' | 'fixed'
    color?: string
    weight?: number
    coupon?: string
}

const props = defineProps<{
    showOnLoad?: boolean
    hideAfterWin?: boolean
    lockAfterWin?: boolean
    storageKey?: string
    pointerColor?: string
    size?: number
    prizes?: Prize[]
    showIfPlayedBefore?: boolean
    startUrl?: string
    actionUrl?: string
}>()

const emit = defineEmits<{
    (e: 'won', prize: Prize): void
    (e: 'close'): void
    (e: 'open'): void
}>()

const BORDER = 16 // 8px left + 8px right
const canvasSize = computed(() => (props.size ?? 320) - BORDER)
const pointerColor = computed(() => props.pointerColor ?? '#f43f5e')
const storagePrefix = computed(() => props.storageKey ?? 'tbs_spin_wheel')

const defaultPrizes: Prize[] = [
    { label: '$10 OFF', type: 'fixed', value: 10, color: '#fb923c', weight: 0 },
    { label: '2% OFF', type: 'percent', value: 2, color: '#93c5fd', weight: 5 },
    { label: '5% OFF', type: 'percent', value: 5, color: '#60a5fa', weight: 1 },
    { label: '30% OFF', type: 'percent', value: 30, color: '#34d399', weight: 0 },
    { label: '40% OFF', type: 'percent', value: 40, color: '#a78bfa', weight: 0 },
    { label: '50% OFF', type: 'percent', value: 50, color: '#f472b6', weight: 0 },
    { label: '100% OFF', type: 'percent', value: 100, color: '#38bdf8', weight: 0 },
    { label: 'Try Again', type: 'percent', value: 0, color: '#9ca3af', weight: 8 },
]

const prizes = computed<Prize[]>(() => props.prizes?.length ? props.prizes : defaultPrizes)
const sliceAngle = computed(() => (2 * Math.PI) / prizes.value.length)

const wheelCanvas = ref<HTMLCanvasElement | null>(null)
const ctx = ref<CanvasRenderingContext2D | null>(null)

const rotation = ref(0)
const spinning = ref(false)
const played = ref(false)
const message = ref('')
const visible = ref(false)

const email = ref('')
const checking = ref(false)
const eligible = ref(false)
const spinsLeft = ref<number | null>(null)
const apiMessage = ref('')
const claiming = ref(false)
const promotion = ref<IPromotion | null>(null)
const codeUsed = ref(false)

const startUrl = computed(() => props.startUrl ?? 'https://sv.lct.vn/tbs/promo/spin/start')
const actionUrl = computed(() => props.actionUrl ?? 'https://sv.lct.vn/tbs/promo/spin/action')

let rafId = 0

function easeOutCubic(t: number) {
    return 1 - Math.pow(1 - t, 3)
}

function drawWheel(rot = 0) {
    const canvas = wheelCanvas.value
    if (!canvas) return
    const context = ctx.value
    if (!context) return

    const W = canvas.width
    const H = canvas.height
    const cx = W / 2
    const cy = H / 2
    const r = Math.min(W, H) / 2

    context.clearRect(0, 0, W, H)

    prizes.value.forEach((p, i) => {
        const start = i * sliceAngle.value + rot
        const end = start + sliceAngle.value

        context.beginPath()
        context.moveTo(cx, cy)
        context.arc(cx, cy, r, start, end)
        context.closePath()
        context.fillStyle = p.color || '#ddd'
        context.fill()

        context.save()
        context.translate(cx, cy)
        context.rotate(start + sliceAngle.value / 2)
        context.font = 'bold 18px Arial'
        context.textAlign = 'center'
        context.textBaseline = 'middle'

        // Draw text border
        context.lineWidth = 4
        context.strokeStyle = '#000000'
        context.strokeText(p.label, r * 0.62, 4)

        // Fill text color
        context.fillStyle = '#ffffff'
        context.fillText(p.label, r * 0.62, 4)
        context.restore()
    })

    context.beginPath()
    context.arc(cx, cy, r * 0.10, 0, Math.PI * 2)
    context.fillStyle = '#0f172a'
    context.fill()
}

function pickWeightedIndex(): number {
    const validPrizes = prizes.value.filter(p => (p.weight ?? 1) > 0)
    const weights = validPrizes.map(p => p.weight ?? 1)
    const sum = weights.reduce((a, b) => a + b, 0)
    let rnd = Math.random() * sum
    for (let i = 0; i < weights.length; i++) {
        if (rnd < weights[i]) {
            // Trả về index của prize gốc
            return prizes.value.indexOf(validPrizes[i])
        }
        rnd -= weights[i]
    }
    // Nếu không trúng gì (fallback)
    return -1
}

function computeStopAngleForIndex(index: number) {
    // Pointer is now at the BOTTOM of the wheel, i.e. +90° (Math.PI/2)
    const sliceCenter = index * sliceAngle.value + sliceAngle.value / 2
    const pointerAngle = Math.PI / 2
    let target = pointerAngle - sliceCenter
    const TAU = Math.PI * 2
    target = ((target % TAU) + TAU) % TAU
    return target
}

function isLose(prize: Prize | null | undefined): boolean {
    if (!prize) return false
    // New schema: type+value
    if (prize.type === 'percent' && Number(prize.value) === 0) return true
    // Back-compat: old `percent` field
    // @ts-ignore
    if (typeof (prize as any).percent === 'number' && (prize as any).percent === 0) return true
    return false
}

function formatPrizeShort(prize: Prize): string {
    // Prefer new schema
    if (prize.type === 'fixed' && typeof prize.value === 'number') {
        return `$${prize.value} OFF`
    }
    if (prize.type === 'percent' && typeof prize.value === 'number' && prize.value > 0) {
        return `${prize.value}% OFF`
    }
    // Back-compat for old `percent`
    // @ts-ignore
    if (typeof (prize as any).percent === 'number') {
        // @ts-ignore
        const oldP = (prize as any).percent
        if (oldP > 0) return `${oldP}% OFF`
        return prize.label
    }
    // fallback to label as-is (label may already include OFF)
    return prize.label
}


async function checkEligibility() {
    apiMessage.value = ''
    if (!email.value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
        apiMessage.value = 'Please enter a valid email.'
        eligible.value = false
        return
    }
    checking.value = true
    try {
        const res = await fetch(startUrl.value, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email.value })
        })
        const data = await res.json().catch(() => ({}))
        // Accept multiple possible field names from backend
        const allowed = data.allowed === true || data.eligible === true || (typeof data.spins === 'number' && data.spins > 0) || (typeof data.spins_remaining === 'number' && data.spins_remaining > 0)
        eligible.value = !!allowed
        spinsLeft.value = (typeof data.spins === 'number') ? data.spins : ((typeof data.spins_remaining === 'number') ? data.spins_remaining : null)
        apiMessage.value = data.message || (eligible.value ? 'You are eligible to spin.' : 'No spins available for this email.')
        // persist email
        persist('email', email.value)
    } catch (e) {
        eligible.value = false
        apiMessage.value = 'Network error. Please try again.'
    } finally {
        checking.value = false
    }
}

async function claimResult(prize: Prize | null) {
    if (!email.value) return
    claiming.value = true
    try {
        const res = await fetch(actionUrl.value, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email.value, prize })
        })
        const data = await res.json().catch(() => ({}))
        if (typeof data.spins === 'number') {
            spinsLeft.value = data.spins
        }
        const promo: IPromotion | undefined = data.promotion
        if (promo) {
            promotion.value = promo
            codeUsed.value = false
            const isPercent = promo.type === 'percent'
            const isFixed = promo.type === 'fixed'
            const valueStr = isPercent ? `${promo.value}%` : (isFixed ? `$${promo.value}` : `${promo.value}`)
            if (promo.code) {
                // Show base message only; code is displayed with a Use button next to it
                message.value = `🎁 You won ${valueStr} OFF`
                persist('code', String(promo.code))
            } else {
                message.value = `🎁 You won ${valueStr} OFF`
            }
        } else {
            message.value = '🙁 Better luck next time!'
        }
    } catch (e) {
        // Keep previous local message on network error
    } finally {
        claiming.value = false
    }
}

function useCode() {
    if (promotion.value) {
        try {
            setPromo(promotion.value)
            if (promotion.value.code) {
                try { localStorage.setItem('cart-promo', String(promotion.value.code)) } catch {}
            }
            codeUsed.value = true
        } catch {}
    }
}

async function spin() {
    if (spinning.value) return
    if (played.value && props.lockAfterWin) return
    if (!eligible.value) {
        message.value = 'Please verify your email to spin.'
        spinning.value = false
        return
    }
    if (spinsLeft.value !== null && spinsLeft.value <= 0) {
        // No spins remaining
        spinning.value = false
        return
    }

    spinning.value = true
    message.value = ''

    const index = pickWeightedIndex()
    const targetRot = computeStopAngleForIndex(index)

    const extraTurns = 4 + Math.floor(Math.random() * 2)
    const TAU = Math.PI * 2
    const startRot = rotation.value % TAU
    const endRot = targetRot + TAU * extraTurns
    const duration = 10000 // spin longer

    const t0 = performance.now()
    const animate = (t: number) => {
        const elapsed = t - t0
        const k = Math.min(1, elapsed / duration)
        const eased = easeOutCubic(k)
        rotation.value = startRot + (endRot - startRot) * eased
        drawWheel(rotation.value)
        if (k < 1) {
            rafId = requestAnimationFrame(animate)
        } else {
            cancelAnimationFrame(rafId)
            if (index === -1) {
                played.value = true
                persist('played', '1')
                message.value = '🙁 Better luck next time!'
                const losePrize = prizes.value.find(p => isLose(p)) || { label: '🙁 Try Again', type: 'percent', value: 0 }
                claimResult(losePrize as Prize)
            } else {
                const prize = prizes.value[index]
                played.value = true
                persist('played', '1')
                persist('result', JSON.stringify(prize))
                if (isLose(prize)) {
                    message.value = '🙁 Better luck next time!'
                    claimResult(prize)
                } else {
                    message.value = `🎁 You won ${formatPrizeShort(prize)}`
                    claimResult(prize).finally(() => emit('won', prize))
                }
            }
            if (props.hideAfterWin) {
                setTimeout(() => close(), 600)
            }
            spinning.value = false
        }
    }

    rafId = requestAnimationFrame(animate)
}

function persist(key: string, val: string) {
    try { localStorage.setItem(`${storagePrefix.value}:${key}`, val) } catch {}
}
function readPersist(key: string) {
    try { return localStorage.getItem(`${storagePrefix.value}:${key}`) } catch { return null }
}

function todayKey() {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

function open() {
    visible.value = true
    emit('open')
}
function close() {
    visible.value = false
    persist('closedAt', todayKey())
    emit('close')
}

function initState() {
    const playedBefore = readPersist('played') === '1'
    played.value = playedBefore
    const res = readPersist('result')
    if (res) {
        try {
            const p: Prize = JSON.parse(res)
            const savedCode = readPersist('code')
            message.value = `🎁 You won ${formatPrizeShort(p)}` + (savedCode ? `\nCode: ${savedCode}` : '')
        } catch {}
    }
    const savedEmail = readPersist('email')
    if (typeof savedEmail === 'string') email.value = savedEmail
    // no-op: we just read closedAt via readPersist when deciding auto-open
    readPersist('closedAt')
}

onMounted(() => {
    const c = wheelCanvas.value
    if (!c) return
    const context = c.getContext('2d')
    if (!context) return
    ctx.value = context
    drawWheel(rotation.value)
    initState()

    if (props.showOnLoad) {
        const playedBefore = readPersist('played') === '1'
        const closedAt = readPersist('closedAt')
        const isClosedToday = closedAt === todayKey()
        if (!isClosedToday && (!playedBefore || props.showIfPlayedBefore)) {
            setTimeout(open, 400)
        }
    }

    const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && visible.value) close()
    }
    window.addEventListener('keydown', onKey)
    onUnmounted(() => {
        window.removeEventListener('keydown', onKey)
        if (rafId) cancelAnimationFrame(rafId)
    })
})

watch(() => [prizes.value.length, canvasSize.value], () => {
    drawWheel(rotation.value)
})

defineExpose({
    open,
    close,
})
</script>

<style scoped>
.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0,0,0,0.6);
    z-index: 9999;
}
.popup-content {
    position: relative;
    width: 100%;
    max-width: 400px;
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 6px 20px rgba(0,0,0,0.25);
    text-align: center;
}
.popup-close {
    position: absolute;
    top: 10px;
    right: 10px;
    border: none;
    background: none;
    font-size: 18px;
    cursor: pointer;
}
.title {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 10px;
}
.subtitle {
    font-size: 14px;
    color: #555;
    margin-bottom: 20px;
}
.wheel-wrapper {
    position: relative;
    margin: 0 auto;
}
.wheel-stage {
    position: relative;
    margin: 0 auto;
    max-width: 320px;
    max-height: 320px;
}
.wheel-pointer {
    position: absolute;
    bottom: -14px; /* slight overlap for the 8px border */
    left: 50%;
    transform: translateX(-50%);
    pointer-events: none;
}
.wheel-canvas {
    width: 100%; /* match .wheel-stage width */
    height: 100%; /* match .wheel-stage height */
    border: 8px solid #333;
    border-radius: 50%;
    box-sizing: border-box; /* include border in the element's box */
    display: block;
}
.spin-btn-wrapper {
    margin-top: 28px;
}
.spin-btn {
    background: #e63946;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    cursor: pointer;
    font-size: 16px;
    font-weight: bold;
}
.spin-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
.use-btn {
    background: #22c55e;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    cursor: pointer;
    font-size: 16px;
    font-weight: bold;
    margin-top: 0;
}
.use-btn:disabled {
    opacity: .6;
    cursor: not-allowed;
}
.result-msg {
    margin-top: 15px;
    font-size: 18px;
    font-weight: bold;
    white-space: pre-line; /* render \n as line breaks */
}
.code-row {
    margin-top: 6px;
    display: inline-flex;
    gap: 10px;
    align-items: center;
}
.disclaimer {
    margin-top: 10px;
    font-size: 12px;
    color: #666;
}
.email-check {
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
    margin: 8px 0 14px;
    flex-wrap: wrap;
}
.email-input {
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    min-width: 220px;
    font-size: 14px;
}
.check-btn {
    background: #2563eb;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 16px;
    cursor: pointer;
    font-weight: 600;
}
.check-btn:disabled { opacity: .6; cursor: not-allowed; }
.api-message { font-size: 14px; color: #374151; width: 100%; text-align: center; }
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
<style lang="scss" scoped></style>